class ProductStoriesVideos {
  constructor(specificContainer = null) {
    this.currentStoryIndex = 0;
    this.isPlaying = false;
    this.storyDuration = 15000;
    this.progressInterval = null;
    this.autoPlayTimeout = null;
    this.videosLoaded = new Set();
    this.specificContainer = specificContainer;
    
    this.touchStartX = null;
    this.touchStartY = null;
    this.minSwipeDistance = 50;
    
    this.init();
  }
  
  init() {
    this.storiesContainer = this.specificContainer || this.getVisibleStoriesContainer();
    if (!this.storiesContainer) {
      return;
    }

    this.modal = this.storiesContainer.querySelector('[data-js-stories-modal]');
    if (!this.modal) {
      return;
    }

    this.videos = this.storiesContainer.querySelectorAll('.product-stories-videos__video');
    this.modalVideos = this.modal.querySelectorAll('.product-stories-modal__video');
    this.progressBars = this.modal.querySelectorAll('.product-stories-modal__progress-fill');
    
    if (this.videos.length === 0) {
      return;
    }
    
    this.setupLazyLoading();
    this.setupEventListeners();
  }

  getVisibleStoriesContainer() {
    const containers = document.querySelectorAll('[data-js-product-stories]');
    
    if (containers.length === 1) {
      return containers[0];
    }
    
    for (let container of containers) {
      const computedStyle = window.getComputedStyle(container);
      
      if (computedStyle.display !== 'none' && 
          computedStyle.visibility !== 'hidden' && 
          computedStyle.opacity !== '0') {
        
        let parent = container.parentElement;
        let isParentVisible = true;
        
        while (parent && parent !== document.body) {
          const parentStyle = window.getComputedStyle(parent);
          if (parentStyle.display === 'none') {
            isParentVisible = false;
            break;
          }
          parent = parent.parentElement;
        }
        
        if (isParentVisible) {
          return container;
        }
      }
    }
    
    return containers[0] || null;
  }

  setupLazyLoading() {
    if (!this.videos || this.videos.length === 0) {
      return;
    }
    
    const thumbnailObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const video = entry.target.querySelector('.product-stories-videos__video');
          setTimeout(() => {
            this.loadVideo(video);
          }, 1000);
          thumbnailObserver.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '50px'
    });

    this.videos.forEach(video => {
      const container = video.closest('.product-stories-videos__item');
      if (container) {
        thumbnailObserver.observe(container);
      }
    });
  }
  
  loadVideo(video) {
    if (!video || this.videosLoaded.has(video)) return;
    
    const dataSrc = video.getAttribute('data-src');
    
    if (dataSrc) {
      video.src = dataSrc;
      video.load();
      this.videosLoaded.add(video);
      
      video.addEventListener('loadeddata', () => {
        this.startThumbnailPlayback(video);
      }, { once: true });
    }
  }

  startThumbnailPlayback(video) {
    if (!video) return;
    
    // Esconder thumbnail e mostrar vídeo
    const container = video.closest('.product-stories-videos__item');
    const thumbnail = container.querySelector('.product-stories-videos__thumbnail');
    if (thumbnail) {
      thumbnail.style.display = 'none';
    }
    video.style.display = 'block';
    
    const playWithTimeLimit = () => {
      video.currentTime = 0;
      video.pause();
      
      setTimeout(() => {
        video.currentTime = 10;
        video.play().catch(() => {});
        
        const timeoutId = setTimeout(() => {
          video.pause();
          video.currentTime = 10;
          setTimeout(() => {
            playWithTimeLimit();
          }, 100);
        }, 10000);
        
        video._thumbnailTimeout = timeoutId;
      }, 2000);
    };
    
    playWithTimeLimit();
  }
  
  loadModalVideo(video) {
    if (!video || this.videosLoaded.has(video)) return Promise.resolve();
    
    return new Promise((resolve) => {
      const dataSrc = video.getAttribute('data-src');
      if (dataSrc) {
        video.classList.add('loading');
        
        video.src = dataSrc;
        video.load();
        this.videosLoaded.add(video);
        
        const onLoadComplete = () => {
          video.classList.remove('loading');
          resolve();
        };
        
        video.addEventListener('loadeddata', onLoadComplete, { once: true });
        video.addEventListener('error', onLoadComplete, { once: true });
      } else {
        resolve();
      }
    });
  }

  setupEventListeners() {
    this.setupThumbnailClickListeners();
    this.setupModalEventListeners();
    this.setupGlobalEventListeners();
  }

  cleanupEventListeners() {
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
    }
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
    if (this.blurHandler) {
      window.removeEventListener('blur', this.blurHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  setupNewEventListeners() {
    this.setupThumbnailClickListeners();
    this.setupModalEventListeners();
    this.setupGlobalEventListeners();
  }

  setupModalEventListeners() {
    if (!this.modal) return;
    
    const closeBtn = this.modal.querySelector('.product-stories-modal__close');
    const overlay = this.modal.querySelector('.product-stories-modal__overlay');
    
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeModal());
    }
    
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closeModal();
        }
      });
    }

    const prevBtn = this.modal.querySelector('.product-stories-modal__prev');
    const nextBtn = this.modal.querySelector('.product-stories-modal__next');
    
    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.previousStory();
      });
      
      prevBtn.addEventListener('touchend', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.previousStory();
      });
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.nextStory();
      });
      
      nextBtn.addEventListener('touchend', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.nextStory();
      });
    }

    this.progressBars.forEach((progressBar, index) => {
      const progressContainer = progressBar.closest('.product-stories-modal__progress-bar');
      if (progressContainer) {
        progressContainer.addEventListener('click', () => {
          this.goToStory(index);
        });
      }
    });

    this.modal.addEventListener('click', (e) => {
      if (e.target.classList.contains('product-stories-modal__prev') ||
          e.target.classList.contains('product-stories-modal__next') ||
          e.target.classList.contains('product-stories-modal__close') ||
          e.target.closest('.product-stories-modal__progress-container')) {
        return;
      }

      const modalContent = this.modal.querySelector('.product-stories-modal__content');
      if (!modalContent) return;
      
      const rect = modalContent.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const contentWidth = rect.width;
      
      if (e.target.classList.contains('product-stories-modal__video')) {
        this.togglePlayPause();
        return;
      }
      
      if (clickX < contentWidth * 0.3) {
        this.previousStory();
      } else if (clickX > contentWidth * 0.7) {
        this.nextStory();
      } else {
        this.togglePlayPause();
      }
    });

    this.modal.addEventListener('touchstart', (e) => {
      if (e.target.classList.contains('product-stories-modal__prev') ||
          e.target.classList.contains('product-stories-modal__next') ||
          e.target.classList.contains('product-stories-modal__close')) {
        return;
      }
      
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    });

    this.modal.addEventListener('touchend', (e) => {
      if (e.target.classList.contains('product-stories-modal__prev') ||
          e.target.classList.contains('product-stories-modal__next') ||
          e.target.classList.contains('product-stories-modal__close')) {
        return;
      }
      
      if (this.touchStartX === null || this.touchStartY === null) return;
      
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      
      const deltaX = this.touchStartX - touchEndX;
      const deltaY = this.touchStartY - touchEndY;
      
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > this.minSwipeDistance) {
        if (deltaX > 0) {
          this.nextStory();
        } else {
          this.previousStory();
        }
      }
      
      this.touchStartX = null;
      this.touchStartY = null;
    });
  }

  setupGlobalEventListeners() {
    this.keydownHandler = (e) => {
      if (!this.modal || !this.modal.classList.contains('active')) return;
      
      switch(e.key) {
        case 'Escape':
          this.closeModal();
          break;
        case 'ArrowLeft':
          this.previousStory();
          break;
        case 'ArrowRight':
          this.nextStory();
          break;
        case ' ':
          e.preventDefault();
          this.togglePlayPause();
          break;
      }
    };

    this.visibilityChangeHandler = () => {
      if (document.hidden && this.isPlaying && this.modal && this.modal.classList.contains('active')) {
        this.pauseCurrentStory();
      }
    };

    this.blurHandler = () => {
      if (this.isPlaying && this.modal && this.modal.classList.contains('active')) {
        this.pauseCurrentStory();
      }
    };

    this.resizeHandler = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.handleResponsiveChange();
      }, 250);
    };

    document.addEventListener('keydown', this.keydownHandler);
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    window.addEventListener('blur', this.blurHandler);
    window.addEventListener('resize', this.resizeHandler);
  }

  handleResponsiveChange() {
    if (this.specificContainer) {
      return;
    }
    
    const newContainer = this.getVisibleStoriesContainer();
    
    if (newContainer && newContainer !== this.storiesContainer) {
      if (this.modal && this.modal.classList.contains('active')) {
        this.closeModal();
      }
      
      this.cleanupEventListeners();
      
      this.storiesContainer = newContainer;
      this.modal = this.storiesContainer.querySelector('[data-js-stories-modal]');
      this.videos = this.storiesContainer.querySelectorAll('.product-stories-videos__video');
      this.modalVideos = this.modal.querySelectorAll('.product-stories-modal__video');
      this.progressBars = this.modal.querySelectorAll('.product-stories-modal__progress-fill');
      
      this.currentStoryIndex = 0;
      this.isPlaying = false;
      
      this.setupLazyLoading();
      this.setupNewEventListeners();
    }
  }

  setupThumbnailClickListeners() {
    this.videos.forEach((video, index) => {
      const item = video.closest('.product-stories-videos__item');
      if (item) {
        const clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openModal(index);
        };
        
        item._clickHandler = clickHandler;
        
        if (item._clickHandler) {
          item.removeEventListener('click', item._clickHandler);
        }
        item.addEventListener('click', clickHandler);
      }
    });
  }
  
  openModal(startIndex = 0) {
    if (!this.modal) {
      return;
    }
    
    this.currentStoryIndex = startIndex;
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
    
    this.setMainZIndex(true);
    
    this.clearThumbnailTimeouts();
    this.videos.forEach(video => {
      video.pause();
    });
    
    this.showStory(this.currentStoryIndex);
    this.playCurrentStory();
  }
  
  closeModal() {
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
    document.body.classList.remove('modal-open');
    
    this.setMainZIndex(false);
    
    this.pauseCurrentStory();
    this.resetProgress();
    
    this.clearThumbnailsIndicator();
    
    this.restartThumbnailPlayback();
  }
  
  clearThumbnailsIndicator() {
    const thumbnailItems = this.storiesContainer.querySelectorAll('.product-stories-videos__item');
    thumbnailItems.forEach(item => {
      item.classList.remove('active');
    });
  }
  
  clearThumbnailTimeouts() {
    this.videos.forEach(video => {
      if (video._thumbnailTimeout) {
        clearTimeout(video._thumbnailTimeout);
        video._thumbnailTimeout = null;
      }
    });
  }
  
  restartThumbnailPlayback() {
    this.videos.forEach(video => {
      if (video.src && this.videosLoaded.has(video)) {
        this.startThumbnailPlayback(video);
      }
    });
  }

  setMainZIndex(isModalOpen) {
    if (isModalOpen) {
      if (this.modal) {
        this.modal.style.zIndex = '999999';
        this.modal.style.position = 'fixed';
      }
      
      const headerContainer = document.querySelector('.site-header-container');
      const breadcrumbMain = document.querySelector('.breadcrumb-main');
      const stickyAddToCart = document.querySelector('.sticky-add-to-cart');
      
      if (headerContainer) {
        headerContainer.style.zIndex = '1';
      }
      if (breadcrumbMain) {
        breadcrumbMain.style.zIndex = '1';
      }
      if (stickyAddToCart) {
        stickyAddToCart.style.zIndex = '1';
      }
    } else {
      if (this.modal) {
        this.modal.style.zIndex = '';
        this.modal.style.position = '';
      }
      
      const headerContainer = document.querySelector('.site-header-container');
      const breadcrumbMain = document.querySelector('.breadcrumb-main');
      const stickyAddToCart = document.querySelector('.sticky-add-to-cart');
      
      if (headerContainer) {
        headerContainer.style.zIndex = '';
      }
      if (breadcrumbMain) {
        breadcrumbMain.style.zIndex = '';
      }
      if (stickyAddToCart) {
        stickyAddToCart.style.zIndex = '';
      }
    }
  }
  
  showStory(index) {
    this.modalVideos.forEach((video, i) => {
      video.classList.remove('active');
      video.pause();
      if (i !== index) {
        video.currentTime = 0;
      }
    });

    if (this.modalVideos[index]) {
      this.modalVideos[index].classList.add('active');
    }

    this.updateThumbnailsIndicator(index);
    this.updateProgressBars();
  }

  updateThumbnailsIndicator(activeIndex) {
    const thumbnailItems = this.storiesContainer.querySelectorAll('.product-stories-videos__item');
    thumbnailItems.forEach((item, index) => {
      if (index === activeIndex) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });
  }
  
  playCurrentStory() {
    const currentVideo = this.modalVideos[this.currentStoryIndex];
    if (!currentVideo) return;

          this.loadModalVideo(currentVideo).then(() => {
        this.isPlaying = true;
        currentVideo.play().catch(() => {});

      this.preloadAdjacentVideos();
      this.startVideoSyncedProgress(currentVideo);

      currentVideo.addEventListener('ended', () => {
        this.nextStory();
      }, { once: true });
    });
  }

  preloadAdjacentVideos() {
    if (this.currentStoryIndex < this.modalVideos.length - 1) {
      const nextVideo = this.modalVideos[this.currentStoryIndex + 1];
      this.loadModalVideo(nextVideo);
    }

    if (this.currentStoryIndex > 0) {
      const prevVideo = this.modalVideos[this.currentStoryIndex - 1];
      this.loadModalVideo(prevVideo);
    }
  }

  pauseCurrentStory() {
    const currentVideo = this.modalVideos[this.currentStoryIndex];
    if (currentVideo) {
      currentVideo.pause();
    }

    this.modalVideos.forEach((video, index) => {
      if (index !== this.currentStoryIndex) {
        video.pause();
      }
    });

    this.isPlaying = false;
    this.stopProgress();
    
    if (this.autoPlayTimeout) {
      clearTimeout(this.autoPlayTimeout);
      this.autoPlayTimeout = null;
    }
  }

  togglePlayPause() {
    if (this.isPlaying) {
      this.pauseCurrentStory();
    } else {
      this.playCurrentStory();
    }
  }

  nextStory() {
    this.pauseCurrentStory();
    
    if (this.currentStoryIndex < this.modalVideos.length - 1) {
      this.currentStoryIndex++;
      this.showStory(this.currentStoryIndex);
      this.playCurrentStory();
    } else {
      this.closeModal();
    }
  }

  previousStory() {
    this.pauseCurrentStory();
    
    if (this.currentStoryIndex > 0) {
      this.currentStoryIndex--;
      this.showStory(this.currentStoryIndex);
      this.playCurrentStory();
    }
  }

  goToStory(index) {
    if (index < 0 || index >= this.modalVideos.length || index === this.currentStoryIndex) {
      return;
    }
    
    this.pauseCurrentStory();
    
    this.currentStoryIndex = index;
    this.showStory(this.currentStoryIndex);
    this.playCurrentStory();
  }

  startVideoSyncedProgress(video) {
    this.stopProgress();
    const currentProgressBar = this.progressBars[this.currentStoryIndex];
    if (!currentProgressBar || !video) return;

    const updateProgress = () => {
      if (video.duration && !isNaN(video.duration)) {
        const percent = (video.currentTime / video.duration) * 100;
        currentProgressBar.style.width = percent + '%';
      } else {
        currentProgressBar.style.width = '0%';
      }
    };

    video.addEventListener('timeupdate', updateProgress);
    video.addEventListener('loadedmetadata', updateProgress);

    this._progressListeners = { video, updateProgress };
  }

  stopProgress() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
    if (this._progressListeners) {
      const { video, updateProgress } = this._progressListeners;
      if (video) {
        video.removeEventListener('timeupdate', updateProgress);
        video.removeEventListener('loadedmetadata', updateProgress);
      }
      this._progressListeners = null;
    }
  }

  updateProgressBars() {
    this.progressBars.forEach((bar, index) => {
      if (index < this.currentStoryIndex) {
        bar.style.width = '100%';
      } else if (index === this.currentStoryIndex) {
        bar.style.width = '0%';
      } else {
        bar.style.width = '0%';
      }
    });
  }

  resetProgress() {
    this.stopProgress();
    this.progressBars.forEach(bar => {
      bar.style.width = '0%';
    });
  }

  destroy() {
    this.cleanupEventListeners();
    this.clearThumbnailTimeouts();
    this.pauseCurrentStory();
    this.resetProgress();
    
    this.storiesContainer = null;
    this.modal = null;
    this.videos = null;
    this.modalVideos = null;
    this.progressBars = null;
    this.videosLoaded.clear();
  }
}

let productStoriesInstances = new Map();

document.addEventListener('DOMContentLoaded', () => {
  initializeProductStories();
});

document.addEventListener('shopify:section:load', () => {
  initializeProductStories();
});

function initializeProductStories() {
  const containers = document.querySelectorAll('[data-js-product-stories]');
  
  containers.forEach(container => {
    if (!productStoriesInstances.has(container)) {
      const instance = new ProductStoriesVideos(container);
      productStoriesInstances.set(container, instance);
    }
  });
  
  productStoriesInstances.forEach((instance, container) => {
    if (!document.contains(container)) {
      instance.destroy?.();
      productStoriesInstances.delete(container);
    }
  });
}

function simpleFallbackInit() {
  const containers = document.querySelectorAll('[data-js-product-stories]');
  
  containers.forEach((container, index) => {
    const items = container.querySelectorAll('.product-stories-videos__item');
    
    items.forEach((item, storyIndex) => {
      const existingHandler = item.getAttribute('data-click-handled');
      if (existingHandler) {
        return;
      }
      
      item.setAttribute('data-click-handled', 'true');
      
      item.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!productStoriesInstances.has(container)) {
          const instance = new ProductStoriesVideos(container);
          productStoriesInstances.set(container, instance);
        }
        
        const instance = productStoriesInstances.get(container);
        if (instance && instance.openModal) {
          instance.openModal(storyIndex);
        }
      });
    });
  });
}

setTimeout(() => {
  if (productStoriesInstances.size === 0) {
    simpleFallbackInit();
  }
}, 2000);
